import api from '@/api/axios';

export interface FileInfo {
  id: number;
  name: string;
  insertedDate: string;
  updatedDate: string;
  downloadUrl: string;
  viewUrl: string;
}

export interface RowStatus {
  id: number;
  nombre: string;
  descripcion: string;
}

export interface CategoryInfo {
  id: number;
  modulo: string;
  nombre: string;
  icono: FileInfo | null;
  rowStatus: RowStatus;
}

export interface LibraryGroupInfo {
  id: number;
  grupo: {
    id: number;
    nombre: string;
    authUuid?: string;
    rowStatus: RowStatus;
  };
  rowStatus: RowStatus;
}

export interface LibraryItem {
  id: number;
  titulo: string;
  descripcion: string;
  categoria: CategoryInfo;
  categoriaArchivo?: {
    id: number;
    code: string;
    name: string;
  };
  archivoDocumento: FileInfo | null;
  archivoPreview: FileInfo | null;
  descargable: boolean;
  obligatorio: boolean;
  duracion?: string;
  link?: string;
  fechaLimite: string | null;
  rowStatus: RowStatus;
  grupos: LibraryGroupInfo[];
  usuarios: unknown[];
}

export interface LibraryPageable {
  pageSize: number;
  pageNumber: number;
  offset: number;
  paged: boolean;
  unpaged: boolean;
}

export interface LibraryDataResponse {
  content: LibraryItem[];
  pageable: LibraryPageable;
  totalPages: number;
  totalElements: number;
  last: boolean;
  first: boolean;
  numberOfElements: number;
  size: number;
  number: number;
  empty: boolean;
}

export interface LibraryApiResponse {
  code: string;
  message: string;
  data: LibraryDataResponse;
}

export interface LibraryCategoryOption {
  id: number;
  modulo: string;
  nombre: string;
  icono: FileInfo | null;
  rowStatus: RowStatus;
}

export interface FileCategoryOption {
  id: number;
  code: string;
  name: string;
  totalFiles: number;
  insertedDate: string;
  updatedDate: string;
}

export interface CategoriesApiResponse {
  code: string;
  message: string;
  data: LibraryCategoryOption[];
}

export interface FileCategoriesApiResponse {
  code: string;
  message: string;
  data: FileCategoryOption[];
}

export interface CreateLibraryItemRequest {
  titulo: string;
  descripcion: string;
  categoriaId: string;
  categoriaArchivoId?: string;
  descargable: boolean;
  obligatorio: boolean;
  duracion?: string;
  link?: string;
  fechaLimite?: string;
  grupos?: number[];
  usuarios?: string[];
  preview: File;
  documento?: File;
}

export interface UpdateLibraryItemRequest {
  titulo: string;
  descripcion: string;
  categoriaId: string;
  categoriaArchivoId?: string;
  descargable: boolean;
  obligatorio: boolean;
  duracion?: string;
  link?: string;
  fechaLimite?: string;
  grupos?: number[];
  usuarios?: string[];
  preview?: File;
  documento?: File;
}

export interface UserSearchResult {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  idAco?: string;
}

export interface UserSearchResponse {
  users: UserSearchResult[];
  total: number;
}

class LibraryService {
  private readonly basePath = '/appy/biblioteca/v1';
  private readonly basePathUpload = '/appy/upload/biblioteca/v1';
  private readonly categoriesPath = '/appy/categoria/v1';
  private readonly fileCategoriesPath = '/appy/modules/files/categories';

  async getItems(page: number = 0, size: number = 15, titulo?: string): Promise<LibraryApiResponse> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('size', String(size));
    if (titulo && titulo.trim()) {
      params.append('titulo', titulo.trim());
    }
    const response = await api.get<LibraryApiResponse>(`${this.basePath}?${params.toString()}`);
    return response.data;
  }

  async getItemById(id: number): Promise<LibraryApiResponse> {
    const response = await api.get<LibraryApiResponse>(`${this.basePath}/${id}`);
    return response.data;
  }

  async getCategories(): Promise<CategoriesApiResponse> {
    const response = await api.get<CategoriesApiResponse>(this.categoriesPath);
    return response.data;
  }

  async getFileCategories(): Promise<FileCategoriesApiResponse> {
    const response = await api.get<FileCategoriesApiResponse>(this.fileCategoriesPath);
    return response.data;
  }

  async createItem(data: CreateLibraryItemRequest): Promise<LibraryApiResponse> {
    const formData = new FormData();
    formData.append('titulo', data.titulo);
    formData.append('categoriaId', data.categoriaId);
    if (data.categoriaArchivoId) {
      formData.append('categoriaArchivoId', data.categoriaArchivoId);
    }
    if (data.descripcion) {
      formData.append('descripcion', data.descripcion);
    }
    formData.append('descargable', String(data.descargable));
    formData.append('obligatorio', String(data.obligatorio));
    if (data.duracion) {
      formData.append('duracion', data.duracion);
    }
    if (data.link) {
      formData.append('link', data.link);
    }
    if (data.fechaLimite) {
      formData.append('fechaLimite', data.fechaLimite);
    }
    if (data.grupos && data.grupos.length > 0) {
      formData.append('grupos', data.grupos.join(','));
    }
    if (data.usuarios && data.usuarios.length > 0) {
      formData.append('usuarios', data.usuarios.join(','));
    }
    formData.append('preview', data.preview);
    if (data.documento) {
      formData.append('documento', data.documento);
    }

    const response = await api.post<LibraryApiResponse>(this.basePathUpload, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async updateItem(id: number, data: UpdateLibraryItemRequest): Promise<LibraryApiResponse> {
    const formData = new FormData();
    formData.append('bibliotecaMaterialId', String(id));
    formData.append('titulo', data.titulo);
    formData.append('categoriaId', data.categoriaId);
    if (data.categoriaArchivoId) {
      formData.append('categoriaArchivoId', data.categoriaArchivoId);
    }
    formData.append('descargable', String(data.descargable));
    formData.append('obligatorio', String(data.obligatorio));
    if (data.descripcion) {
      formData.append('descripcion', data.descripcion);
    }
    if (data.duracion) {
      formData.append('duracion', data.duracion);
    }
    if (data.link) {
      formData.append('link', data.link);
    }
    if (data.fechaLimite) {
      formData.append('fechaLimite', data.fechaLimite);
    }
    if (data.grupos && data.grupos.length > 0) {
      formData.append('grupos', data.grupos.join(','));
    }
    if (data.usuarios && data.usuarios.length > 0) {
      formData.append('usuarios', data.usuarios.join(','));
    }
    if (data.preview) {
      formData.append('preview', data.preview);
    }
    if (data.documento) {
      formData.append('documento', data.documento);
    }

    const response = await api.put<LibraryApiResponse>(`${this.basePath}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async deleteItem(id: number): Promise<void> {
    await api.delete(`${this.basePath}/${id}`);
  }

  async addGroupToItem(itemId: number, groupId: number): Promise<void> {
    await api.post(`${this.basePath}/${itemId}/grupos/${groupId}`);
  }

  async removeGroupFromItem(itemId: number, groupId: number): Promise<void> {
    await api.delete(`${this.basePath}/${itemId}/grupos/${groupId}`);
  }

  async searchUsers(search: string): Promise<UserSearchResponse> {
    const params = new URLSearchParams();
    params.append('page', '0');
    params.append('size', '10');
    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    const response = await api.get<{ data: UserSearchResponse }>(`/admin/users?${params.toString()}`);
    return response.data.data;
  }

  async getItemsByGroup(grupoId: string): Promise<LibraryApiResponse> {
    const response = await api.get<LibraryApiResponse>(`${this.basePath}?grupoId=${grupoId}`);
    return response.data;
  }

  async removeItemFromGroup(itemId: number, grupoId: string): Promise<void> {
    await api.delete(`${this.basePath}/${itemId}/grupos/${grupoId}`);
  }
}

export const libraryService = new LibraryService();
export default libraryService;